// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Link,
  Font,
} from "@react-pdf/renderer";

import Logo from "../../assets/img/wso2-logo-colored.png";
import Background from "../../assets/img/quartely-award-background.png";
import {
  certificateFooter, signatureMain
} from "../../config/constant";
import PlusJakartaRegular from "../../assets/fonts/PlusJakartaSans-Regular.ttf";
import PlusJakartaExtraBold from "../../assets/fonts/PlusJakartaSans-ExtraBold.ttf";
import PlusJakartaBold from "../../assets/fonts/PlusJakartaSans-Bold.ttf";
import PlusJakartaSemiBold from "../../assets/fonts/PlusJakartaSans-SemiBold.ttf";
import PlusJakartaMedium from "../../assets/fonts/PlusJakartaSans-Medium.ttf";

Font.register({
  family: "PlusJakarta",
  fonts: [
    {
      src: PlusJakartaRegular,
    },
    {
      src: PlusJakartaExtraBold,
      fontWeight: "heavy",
    },
    {
      src: PlusJakartaBold,
      fontWeight: "bold",
    },
    {
      src: PlusJakartaSemiBold,
      fontWeight: "semibold",
    },
    {
      src: PlusJakartaMedium,
      fontWeight: "medium",
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    fontFamily: "PlusJakarta",
  },
  section1: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  section2: {
    margin: 20,
    padding: 20,
    flexGrow: 1,
    borderRadius: 10,
  },
  container: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  topLogo: {
    marginTop: 20,
    width: 100,
  },
  headingText: {
    color: "black",
    marginTop: 10,
    fontSize: 31,
    fontWeight: "extrabold",
  },
  quarterText: {
    color: "black",
    marginTop: 0,
    fontSize: 31,
    fontWeight: "extrabold",
  },
  subHeadingText: {
    fontWeight: "medium",
    marginTop: 80,
    color: "#000",
  },
  name: {
    marginTop: 50,
    color: "#000",
    paddingBottom: 15,
    fontSize: 30,
    fontWeight: "bold",
  },
  awardedText: {
    fontSize: 12,
    color: "#000",
    fontWeight: "medium",
    width: 400,
  },
  extraDetailsTop: {
    marginTop: 50,
    fontSize: 12,
    color: "#000",
    fontWeight: "semibold",
  },
  extraDetails: {
    fontSize: 12,
    color: "#000",
    marginTop: 5,
    fontWeight: "semibold",
  },
  certName: {
    marginTop: 15,
    color: "#000",
    fontSize: 17,
    fontWeight: "bold",
    marginBottom: 50,
    width: 500,
  },
  badge: {
    position: "absolute",
    width: 200,
    top: 180,
    right: 40,
  },
  link: {
    textDecoration: "none",
    color: "#000",
  },
  footer: {
    alignSelf: "flex-end",
  },
  footerDate: {
    alignSelf: "flex-end",
  },
  footerText: {
    color: "#000",
    fontSize: 12,
    fontWeight: "semibold",
    marginLeft: "auto",
    marginRight: "auto",
    width: 200,
  },
  date: {
    color: "#000",
    fontSize: 12,
    marginLeft: "auto",
    marginRight: "auto",
    width: 200,
    fontWeight: "semibold",
  },
  footerSection: {
    marginTop: 50,
    flexDirection: "row",
    fontWeight: "semibold",
  },
  footerCaption: {
    color: "#000",
    fontSize: 12,
    fontWeight: "semibold",
  },
  signature: {
    width: 140,
    marginRight: "auto",
    marginTop: 20,
    position: "relative",
    bottom: -10,
    marginBottom: 10,
  },
  background: {
    position: "absolute",
    width: "100%",
    height: "100%",
  },
});

const QuarterlyAwards = (props: { certification: any }) => {
  if (!props.certification) {
    return (
      <Document>
        <Page style={styles.page}>
          <Text>Error: No certification data available</Text>
        </Page>
      </Document>
    );
  }

  const { certification } = props;
  const contact = certification.contact || { firstName: "Unknown", lastName: "User" };

  return (
    <Document>
      <Page orientation="landscape" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.section1}>
            <Image src={Background} style={styles.background} />
          </View>
          <View style={styles.section2}>
            <Image src={Logo} style={styles.topLogo} />
            <View style={styles.headingText}>
              <Text>CERTIFIED QUARTERLY AWARDS</Text>
            </View>
            <View style={styles.quarterText}>
              <Text>
                {certification.quarter} {certification.year}
              </Text>
            </View>
            <View style={styles.name}>
              <Text>
                {`${contact.firstName || "Unknown"} ${contact.lastName || "User"}`}
              </Text>
            </View>
            <View>
              <Text style={styles.awardedText}>
                Has demonstrated exceptional commitment to gaining WSO2 product knowledge by achieving
                multiple certifications in {certification.quarter}{" "}
                {certification.year}.
              </Text>
            </View>
            {certification.logo && <Image src={certification.logo} style={styles.badge} />}
            <Text style={styles.extraDetailsTop}>
              Validate at{" "}
              <Link style={styles.link} src="https://certification.wso2.com">
                certification.wso2.com
              </Link>
            </Text>
            <Text style={styles.extraDetails}>
              Certification ID : {certification.certificationId || "N/A"}
            </Text>
            <View style={styles.footerSection}>
              <View style={styles.footerDate}>
                <Text style={styles.date}>{certification.date || "N/A"}</Text>
                <Text style={styles.footerText}>Date</Text>
              </View>
              <View style={styles.footer}>
                <Image src={signatureMain} style={styles.signature} />
                <Text style={styles.footerText}>{certificateFooter.main.name}</Text>
                <Text style={styles.footerCaption}>{certificateFooter.main.designation}</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default QuarterlyAwards;
